import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api-fetch.js';
import { getAuthHeaders } from '@/identity.js';

import {
  DriveFileRestoreError,
  driveErrorMessage,
  driveFileItemFromWire,
  driveFileVersionsFromWire,
  type DriveFileItem,
  type DriveFileItemWire,
  type DriveFileVersionList,
} from './drive-files.js';

/**
 * Client for the SharePoint browse endpoints used by the workspace Knowledge picker (cti-sync-service,
 * reached via the gateway at `/v1/sharepoint/*`). The picker walks three levels — sites → drives →
 * items (folders/files) — so an editor can point a knowledge source at a SharePoint folder or file
 * without pasting raw Graph ids.
 *
 * REST is snake_case on the wire (`site_id`, `drive_id`, `item_id`, `is_folder`, `web_url`); this maps
 * it to the camelCase the SPA uses. When a field crosses this boundary the exact snake_case key must
 * match the service response — a typo is silent data loss the typechecker can't catch.
 *
 * Access note: every browse call carries the target `workspace_id`. The service resolves that
 * workspace's SharePoint connect token (per-workspace OAuth) and authorizes on workspace membership;
 * without the `workspace_id` the service falls back to the corporate app-only path, so the query param
 * is load-bearing, not optional. The picker surfaces any error rather than pretending the tree is empty.
 *
 * All three routes are paged: the caller asks for a `limit` and gets back a `next_cursor`. **A list
 * ends when `next_cursor` is `null` and at no other point** — Graph does hand out pages that hold zero
 * rows while still carrying a cursor, so a caller that stops on an empty page loses the rest of the
 * tree (on one live tenant 15 of 41 drive pages were empty and all 26 libraries were only found by
 * walking to `null`).
 */

const API_BASE = '/v1/sharepoint';

/**
 * Rows per browse request. The service caps `limit` at 500 and answers 422 — not 400 — for anything
 * outside 1..500, so this stays well inside the range.
 */
export const SHAREPOINT_PAGE_SIZE = 100;

/** Paging knobs shared by the three browse calls. */
export interface SharePointPageOptions {
  limit?: number;
  /** The previous page's `nextCursor`. Absent, `null` or empty means "first page". */
  cursor?: string | null;
  /**
   * Which of the workspace's SharePoint connections to browse. `null` or absent leaves the choice to
   * the service, which resolves the workspace's shared connection — the only behaviour there was before
   * connections were named, and what every surface that browses one tree still wants.
   *
   * Naming it is what makes a customer's tenant reachable at all: Graph ids are unique inside a
   * tenant, not between tenants, so a folder is only addressable together with the connection it lives
   * in.
   */
  connectionId?: string | null;
}

/** One page of browse results. `nextCursor === null` means this was the last page. */
export interface SharePointPage<T> {
  items: T[];
  nextCursor: string | null;
}

/** A SharePoint site as the SPA consumes it (camelCase). */
export interface SharePointSite {
  siteId: string;
  name: string;
  webUrl: string;
}

/** A document library (drive) within a site. */
export interface SharePointDrive {
  driveId: string;
  name: string;
  webUrl: string;
}

/** A folder or file within a drive. `isFolder` decides whether it can be drilled into. */
export interface SharePointItem {
  itemId: string;
  name: string;
  isFolder: boolean;
  webUrl: string;
}

/**
 * One hit of a file search. It is an item that carries its OWN site and library, because a search runs
 * across everything the connection reaches rather than inside the level the caller is standing on — so
 * nothing about where a hit lives can be inherited from the surrounding position.
 *
 * That also means a hit is a complete address on its own: attaching it to a message, shelving it as a
 * knowledge source, opening it in SharePoint or linking a colleague to it takes no further request.
 *
 * `parentId` is the folder the hit sits in, which is what makes the link possible: our links address a
 * file as "this file, inside that folder". It is empty on the rare hit Graph names no parent for, and a
 * caller with nothing to point at leaves the link off rather than building one that opens the library
 * root and highlights nothing.
 */
export interface SharePointSearchHit extends SharePointItem {
  siteId: string;
  driveId: string;
  parentId: string;
}

/** The snake_case shapes the service returns. */
interface SharePointSiteWire {
  site_id: string;
  name: string;
  web_url: string;
}

interface SharePointDriveWire {
  drive_id: string;
  name: string;
  web_url: string;
}

interface SharePointItemWire {
  item_id: string;
  name: string;
  is_folder: boolean;
  web_url: string;
}

interface SharePointSearchHitWire extends SharePointItemWire {
  site_id: string;
  drive_id: string;
  parent_id: string;
}

/**
 * Request headers for every call. Delegates the bearer to {@link getAuthHeaders} so each request gets a
 * fresh platform JWT (silently re-exchanged inside Teams) rather than a possibly-stale stored value.
 */
async function headers(): Promise<Record<string, string>> {
  return { 'Content-Type': 'application/json', ...(await getAuthHeaders()) };
}

/**
 * Thrown by every call here on a non-ok response, carrying the status so the picker can tell the
 * cases apart: **412** is "the caller has no SharePoint connection here" — a claim about the reader,
 * not about the workspace, since everybody connects under their own account — **403** is a viewer
 * without editor rights, anything else is a genuine Graph/transport failure. Without the status the picker had one message for all three and blamed permissions for
 * a missing connection.
 *
 * {@link copySharePointFile} needs the status for a second reason: it decides whether the same file is
 * worth sending again. **503** is the shell, identity or Graph being briefly unavailable, so the copy
 * can simply be retried; **409** (the name is taken), **403** (no write right), **413** (too large) and
 * **404** (no such connection) are answers, and repeating the call repeats them.
 *
 * The `message` is the server's own sentence and is shown to the user verbatim — it names what to do
 * next, which nothing on this side of the wire knows.
 */
export class SharePointApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'SharePointApiError';
  }
}

/**
 * The query params every browse call shares. The cursor is passed through **verbatim** — it is an
 * opaque server token, and the service rejects anything it did not mint. It is left out entirely when
 * there is none: an empty `cursor=` answers 400, so "no cursor" must mean "no parameter".
 */
function browseParams(
  workspaceId: string,
  options?: SharePointPageOptions,
): URLSearchParams {
  const params = new URLSearchParams({
    workspace_id: workspaceId,
    limit: String(options?.limit ?? SHAREPOINT_PAGE_SIZE),
  });
  if (options?.cursor) params.set('cursor', options.cursor);
  if (options?.connectionId) {
    params.set('connection_id', options.connectionId);
  }
  return params;
}

/** `next_cursor` off the wire, defaulting to "last page" when the key is absent. */
function nextCursorOf(body: { next_cursor?: string | null }): string | null {
  return body.next_cursor ?? null;
}

/**
 * What each stable place-error code means, in the words a person can act on.
 *
 * The place endpoints answer `{ error: { code } }` and carry no sentence, unlike the browse
 * endpoints, which answer `{ message }`. Without this map the fallback below printed the JSON body
 * itself on screen — `{"error":{"code":"SHAREPOINT_PLACE_CONFLICT"}}` is what a person saw after
 * pressing Add on a folder inside a site they had already added.
 */
const PLACE_ERROR_MESSAGES: Record<string, string> = {
  SHAREPOINT_PLACE_NOT_ALLOWED:
    'Only the person who connected this SharePoint can change what it exposes.',
  SHAREPOINT_PLACE_NOT_FOUND: 'That place is no longer on the list.',
  SHAREPOINT_PLACE_CONFLICT:
    'That overlaps something already on the list — one of the two has to go first.',
  SHAREPOINT_PLACE_ALIAS_TAKEN:
    'Another place on this list already has that name. Rename one of them in its settings.',
  SHAREPOINT_PLACE_LIMIT_REACHED:
    'This connection is already exposing as many places as it may. Remove one to add another.',
  SHAREPOINT_PLACE_ACCESS_REVOKED:
    'This SharePoint connection no longer grants the access this needs.',
  SHAREPOINT_PLACE_INVALID_REQUEST: 'SharePoint refused that address.',
  SHAREPOINT_SETUP_SESSION_EXPIRED:
    'Browsing timed out. Close this and open it again.',
  SHAREPOINT_PLACE_STORE_UNAVAILABLE:
    'The place list could not be reached just now. Try again in a moment.',
};

async function errorMessage(res: Response, fallback: string): Promise<string> {
  const text = await res.text().catch(() => '');
  if (!text.trim()) return `${fallback} (${res.status})`;
  try {
    const body = JSON.parse(text) as {
      message?: string | string[];
      error?: { code?: string };
    };
    if (Array.isArray(body.message)) return body.message.join('; ');
    if (typeof body.message === 'string' && body.message.trim())
      return body.message;
    const code = body.error?.code;
    if (typeof code === 'string') {
      return PLACE_ERROR_MESSAGES[code] ?? `${fallback} (${code})`;
    }
  } catch {
    /* not JSON */
  }
  /** Never the raw body: an unparsed payload is the server's wire, not a sentence for a person. */
  return `${fallback} (${res.status})`;
}

/**
 * List (optionally search) the SharePoint sites the workspace's connect can see. `workspaceId` is
 * required — it selects the delegated token; omitting it drops the service to the corporate app-only
 * scope. Response is `{ sites: [...], next_cursor }`.
 */
export async function listSharePointSites(
  search: string,
  workspaceId: string,
  options?: SharePointPageOptions,
): Promise<SharePointPage<SharePointSite>> {
  const params = browseParams(workspaceId, options);
  const trimmed = search.trim();
  if (trimmed) params.set('search', trimmed);
  const res = await fetch(`${API_BASE}/sites?${params.toString()}`, {
    headers: await headers(),
  });
  if (!res.ok)
    throw new SharePointApiError(
      res.status,
      await errorMessage(res, 'Failed to load SharePoint sites'),
    );
  const body = (await res.json()) as {
    sites?: SharePointSiteWire[];
    next_cursor?: string | null;
  };
  return {
    items: (body.sites ?? []).map((wire) => ({
      siteId: wire.site_id,
      name: wire.name,
      webUrl: wire.web_url,
    })),
    nextCursor: nextCursorOf(body),
  };
}

/**
 * List a site's document libraries (drives) under the workspace's connect. Response is
 * `{ drives: [...], next_cursor }`.
 */
export async function listSharePointDrives(
  siteId: string,
  workspaceId: string,
  options?: SharePointPageOptions,
): Promise<SharePointPage<SharePointDrive>> {
  const params = browseParams(workspaceId, options);
  const res = await fetch(
    `${API_BASE}/sites/${encodeURIComponent(siteId)}/drives?${params.toString()}`,
    { headers: await headers() },
  );
  if (!res.ok)
    throw new SharePointApiError(
      res.status,
      await errorMessage(res, 'Failed to load document libraries'),
    );
  const body = (await res.json()) as {
    drives?: SharePointDriveWire[];
    next_cursor?: string | null;
  };
  return {
    items: (body.drives ?? []).map((wire) => ({
      driveId: wire.drive_id,
      name: wire.name,
      webUrl: wire.web_url,
    })),
    nextCursor: nextCursorOf(body),
  };
}

/**
 * List the folders and files under a parent within a drive. `parent` is a folder `item_id`, or the
 * literal `'root'` for the drive's top level. Response is `{ items: [...], next_cursor }`.
 */
export async function listSharePointItems(
  driveId: string,
  parent: string,
  workspaceId: string,
  options?: SharePointPageOptions,
): Promise<SharePointPage<SharePointItem>> {
  const params = browseParams(workspaceId, options);
  params.set('parent', parent || 'root');
  const res = await fetch(
    `${API_BASE}/drives/${encodeURIComponent(driveId)}/items?${params.toString()}`,
    { headers: await headers() },
  );
  if (!res.ok)
    throw new SharePointApiError(
      res.status,
      await errorMessage(res, 'Failed to load items'),
    );
  const body = (await res.json()) as {
    items?: SharePointItemWire[];
    next_cursor?: string | null;
  };
  return {
    items: (body.items ?? []).map((wire) => ({
      itemId: wire.item_id,
      name: wire.name,
      isFolder: wire.is_folder,
      webUrl: wire.web_url,
    })),
    nextCursor: nextCursorOf(body),
  };
}

/**
 * The bytes of one SharePoint file, for the file viewer. The route belongs to cti-sharepoint-mcp
 * (`/v1/sharepoint/files/*`, not the knowledge picker's browse routes), which reads the file with the
 * caller's own connection, so `connection_id` names the SharePoint the row was found in. Its query is
 * strict: `workspace_id` and `connection_id` and nothing else. It goes through {@link apiFetch} for the
 * same reason the places calls do — a download can outlive a token.
 */
export async function fetchSharePointFileBlob(
  workspaceId: string,
  connectionId: string | null,
  driveId: string,
  itemId: string,
): Promise<Blob> {
  const params = new URLSearchParams({ workspace_id: workspaceId });
  if (connectionId) params.set('connection_id', connectionId);
  const res = await apiFetch(
    `${API_BASE}/files/drives/${encodeURIComponent(driveId)}/items/${encodeURIComponent(itemId)}/content?${params.toString()}`,
  );
  if (!res.ok)
    throw new SharePointApiError(
      res.status,
      await errorMessage(res, 'Failed to load the file'),
    );
  return res.blob();
}

/**
 * One page of file-search results. Searching pages by a numeric OFFSET, not by the opaque cursor the
 * three browse routes use: the next page is the same `q` and `limit` again at `nextOffset`, and
 * `nextOffset === null` is the end. Reusing a browse pager here would page the wrong way round.
 */
export interface SharePointSearchPage {
  items: SharePointSearchHit[];
  nextOffset: number | null;
}

/** Paging knobs for a file search. `offset` is the previous page's `nextOffset`. */
export interface SharePointSearchOptions {
  limit?: number;
  offset?: number;
  /** Which connection to search, exactly as in {@link SharePointPageOptions}. */
  connectionId?: string | null;
}

/**
 * Search the files the workspace's SharePoint connection reaches, by NAME and by CONTENTS — it is
 * Microsoft Search, not a filter over a list already on screen, so every keystroke that settles is a call
 * to the service. `workspaceId` selects the connection the same way it does for browsing.
 *
 * The service deliberately returns no total: Graph's own count dances between pages, so there is no
 * honest number of results to show. What comes back is a page and whether another one exists.
 *
 * Validation failures answer **422** — a blank `q`, a `limit` outside 1..500, a negative `offset` — and
 * the status travels on {@link SharePointApiError} so the surfaces can still tell a 412 (no connection)
 * apart from a refusal to run the query.
 */
export async function searchSharePointFiles(
  query: string,
  workspaceId: string,
  options?: SharePointSearchOptions,
): Promise<SharePointSearchPage> {
  const params = new URLSearchParams({
    workspace_id: workspaceId,
    q: query.trim(),
    limit: String(options?.limit ?? SHAREPOINT_PAGE_SIZE),
    offset: String(options?.offset ?? 0),
  });
  if (options?.connectionId) {
    params.set('connection_id', options.connectionId);
  }
  const res = await fetch(`${API_BASE}/search?${params.toString()}`, {
    headers: await headers(),
  });
  if (!res.ok)
    throw new SharePointApiError(
      res.status,
      await errorMessage(res, 'Failed to search SharePoint'),
    );
  const body = (await res.json()) as {
    items?: SharePointSearchHitWire[];
    next_offset?: number | null;
  };
  return {
    items: (body.items ?? []).map((wire) => ({
      siteId: wire.site_id,
      driveId: wire.drive_id,
      parentId: wire.parent_id ?? '',
      itemId: wire.item_id,
      name: wire.name,
      isFolder: wire.is_folder,
      webUrl: wire.web_url,
    })),
    nextOffset: body.next_offset ?? null,
  };
}

/**
 * What happens when the target folder already holds a file of that name: `fail` refuses the copy (the
 * default, and what creating a file in SharePoint already does), `rename` saves it under a free name.
 * There is no third option — replacing a file somebody else may be working on is its own decision, not a
 * conflict strategy.
 */
export type SharePointCopyConflict = 'fail' | 'rename';

/** The file being copied, addressed inside the connection it lives in. */
export interface SharePointCopySource {
  connectionId: string;
  driveId: string;
  itemId: string;
}

/** Where the copy goes. */
export interface SharePointCopyTarget {
  connectionId: string;
  driveId: string;
  /**
   * The folder the copy lands in. `null` is the library's root, and the key is then left OFF the wire
   * entirely — the service reads an absent `target_parent_item_id` as the root and rejects an empty
   * string.
   */
  parentItemId?: string | null;
}

/** One copy request, in the camelCase the SPA works in. */
export interface SharePointCopyInput {
  workspaceId: string;
  source: SharePointCopySource;
  target: SharePointCopyTarget;
  /** The name to save under; omitted keeps the source file's own name. */
  name?: string;
  /** Defaults to `fail`, the same default the service applies. */
  onConflict?: SharePointCopyConflict;
}

/**
 * A file at one end of a copy. `connectionId` and `connectionLabel` are nullable because identity can
 * answer without a connection list, and the result then names the file without naming its SharePoint.
 */
export interface SharePointCopyPlace {
  connectionId: string | null;
  connectionLabel: string | null;
  driveId: string;
  itemId: string;
  name: string;
}

/**
 * What came back from a copy: both ends of it, the name that was asked for, and whether the file had to
 * be saved under a different one.
 *
 * Everything past the copy's address is nullable. Graph omits `web_url`, `size_bytes`, `mime_type` and
 * `modified_at` whenever it did not report them, so a surface has to be able to show a finished copy it
 * cannot link to.
 */
export interface SharePointCopyResult {
  source: SharePointCopyPlace;
  copy: SharePointCopyPlace & {
    webUrl: string | null;
    sizeBytes: number | null;
    mimeType: string | null;
    modifiedAt: string | null;
  };
  requestedName: string;
  /** True when `copy.name` differs from {@link requestedName} — the `rename` conflict rule fired. */
  renamed: boolean;
}

/** The snake_case shapes the copy route returns. Every field is optional: this is a cast, not a schema. */
interface SharePointCopyPlaceWire {
  connection_id?: string | null;
  connection_label?: string | null;
  drive_id?: string;
  item_id?: string;
  name?: string;
  web_url?: string;
  size_bytes?: number;
  mime_type?: string;
  modified_at?: string;
}

interface SharePointCopyResultWire {
  source?: SharePointCopyPlaceWire;
  copy?: SharePointCopyPlaceWire;
  requested_name?: string;
  renamed?: boolean;
}

/** Maps one end of a copy off the wire, defaulting every missing key rather than emitting `undefined`. */
function copyPlaceFromWire(
  wire?: SharePointCopyPlaceWire,
): SharePointCopyPlace {
  return {
    connectionId: wire?.connection_id ?? null,
    connectionLabel: wire?.connection_label ?? null,
    driveId: wire?.drive_id ?? '',
    itemId: wire?.item_id ?? '',
    name: wire?.name ?? '',
  };
}

/**
 * Copy ONE file from one of the workspace's SharePoints into another — the server-side operation behind
 * the transfer screen (`POST /v1/sharepoint/copy` on `cti-sharepoint-mcp`, routed by the gateway). The
 * bytes are downloaded and re-uploaded by the service; nothing about the file passes through the browser,
 * which is why this takes addresses and returns a result rather than moving any content.
 *
 * The workspace travels in the BODY, not in a header: this route reads `workspace_id` beside the rest of
 * the fields and ignores `X-Workspace-Id`.
 *
 * Both connections are named on every call, because a `drive_id` is unique inside a tenant and not
 * between tenants — "the workspace's SharePoint" stopped being an address the moment a workspace could
 * reach two of them.
 *
 * It copies one file per call by design, so a batch is the caller's queue: a failure belongs to its own
 * file, and the rest of the selection carries on.
 */
export async function copySharePointFile(
  input: SharePointCopyInput,
): Promise<SharePointCopyResult> {
  const body: Record<string, string> = {
    workspace_id: input.workspaceId,
    source_connection_id: input.source.connectionId,
    source_drive_id: input.source.driveId,
    source_item_id: input.source.itemId,
    target_connection_id: input.target.connectionId,
    target_drive_id: input.target.driveId,
    on_conflict: input.onConflict ?? 'fail',
  };
  if (input.target.parentItemId) {
    body.target_parent_item_id = input.target.parentItemId;
  }
  if (input.name) body.name = input.name;
  const res = await fetch(`${API_BASE}/copy`, {
    method: 'POST',
    headers: await headers(),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    throw new SharePointApiError(
      res.status,
      await errorMessage(res, 'Failed to copy the file'),
    );
  }
  const wire = (await res.json()) as SharePointCopyResultWire;
  const copy = copyPlaceFromWire(wire.copy);
  return {
    source: copyPlaceFromWire(wire.source),
    copy: {
      ...copy,
      webUrl: wire.copy?.web_url ?? null,
      sizeBytes: wire.copy?.size_bytes ?? null,
      mimeType: wire.copy?.mime_type ?? null,
      modifiedAt: wire.copy?.modified_at ?? null,
    },
    requestedName: wire.requested_name ?? copy.name,
    renamed: wire.renamed === true,
  };
}

/**
 * Query-key factory so the picker's three levels cache and invalidate independently.
 *
 * Every key carries BOTH the workspace and the connection. The workspace is there because switching it
 * has to re-fetch under the new connect rather than serve another workspace's tree from cache; the
 * connection is there because one workspace now reaches more than one SharePoint, and two connections
 * list different sites, different libraries and different folders under identical Graph ids. Without
 * that dimension, switching the connection in the picker would show the previous tenant's folders out
 * of cache. `null` — "whichever connection the service resolves" — is a key of its own, `''`.
 */
export const sharePointKeys = {
  sites: (workspaceId: string, connectionId: string | null, search: string) =>
    [
      'sharepoint',
      'sites',
      workspaceId,
      connectionId ?? '',
      search.trim(),
    ] as const,
  drives: (workspaceId: string, connectionId: string | null, siteId: string) =>
    ['sharepoint', 'drives', workspaceId, connectionId ?? '', siteId] as const,
  items: (
    workspaceId: string,
    connectionId: string | null,
    driveId: string,
    parent: string,
  ) =>
    [
      'sharepoint',
      'items',
      workspaceId,
      connectionId ?? '',
      driveId,
      parent || 'root',
    ] as const,
  search: (workspaceId: string, connectionId: string | null, query: string) =>
    [
      'sharepoint',
      'search',
      workspaceId,
      connectionId ?? '',
      query.trim(),
    ] as const,
};

/**
 * The next page param for every browse list: the page's own `nextCursor`, which TanStack reads as
 * "no more pages" when it is `null`. Deliberately blind to how many rows the page held — page length
 * is not an end-of-list signal here.
 */
function nextPageParam<T>(lastPage: SharePointPage<T>): string | null {
  return lastPage.nextCursor;
}

/**
 * TanStack infinite-query options for the site list. `enabled` lets the picker defer the call until
 * open.
 */
export function sharePointSitesQueryOptions(
  workspaceId: string,
  connectionId: string | null,
  search: string,
  enabled: boolean,
) {
  return infiniteQueryOptions({
    queryKey: sharePointKeys.sites(workspaceId, connectionId, search),
    queryFn: ({ pageParam }) =>
      listSharePointSites(search, workspaceId, {
        cursor: pageParam,
        connectionId,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: nextPageParam,
    enabled,
    staleTime: 60_000,
  });
}

/** TanStack infinite-query options for a site's drives, deferred until a site is chosen. */
export function sharePointDrivesQueryOptions(
  workspaceId: string,
  connectionId: string | null,
  siteId: string | null,
) {
  return infiniteQueryOptions({
    queryKey: sharePointKeys.drives(workspaceId, connectionId, siteId ?? ''),
    queryFn: ({ pageParam }) =>
      listSharePointDrives(siteId!, workspaceId, {
        cursor: pageParam,
        connectionId,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: nextPageParam,
    enabled: siteId !== null,
    staleTime: 60_000,
  });
}

/**
 * TanStack infinite-query options for a drive's items under a parent, deferred until a drive is
 * chosen.
 */
export function sharePointItemsQueryOptions(
  workspaceId: string,
  connectionId: string | null,
  driveId: string | null,
  parent: string,
) {
  return infiniteQueryOptions({
    queryKey: sharePointKeys.items(
      workspaceId,
      connectionId,
      driveId ?? '',
      parent,
    ),
    queryFn: ({ pageParam }) =>
      listSharePointItems(driveId!, parent, workspaceId, {
        cursor: pageParam,
        connectionId,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: nextPageParam,
    enabled: driveId !== null,
    staleTime: 30_000,
  });
}

/**
 * TanStack infinite-query options for a file search. It runs only on a query with something in it — the
 * service answers 422 to a blank `q`, and an empty search box means "show the tree", not "search for
 * nothing". Paging is by offset, so the page param is a number and the first page is 0.
 */
export function sharePointSearchQueryOptions(
  workspaceId: string,
  connectionId: string | null,
  query: string,
) {
  const trimmed = query.trim();
  return infiniteQueryOptions({
    queryKey: sharePointKeys.search(workspaceId, connectionId, trimmed),
    queryFn: ({ pageParam }) =>
      searchSharePointFiles(trimmed, workspaceId, {
        offset: pageParam,
        connectionId,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage: SharePointSearchPage) => lastPage.nextOffset,
    enabled: trimmed.length > 0,
    staleTime: 30_000,
  });
}

/**
 * Allowed places on a SharePoint connection: an explicit allow-list of sites, libraries, folders or
 * single files the agent and every browse surface may reach, at `GET/POST /v1/sharepoint/places`,
 * `PATCH`/`DELETE /v1/sharepoint/places/:place_id` and `PUT /v1/sharepoint/place-policy`
 * (`cti-sharepoint-mcp`, routed by the gateway).
 *
 * These calls use {@link apiFetch} rather than this file's own `headers()` helper above — a
 * deliberate split, not an oversight: `apiFetch` carries pre-emptive JWT renewal and a 401 retry
 * that the older calls in this file do not, and migrating those is its own change.
 *
 * Only the connection's OWNER may add, rename, re-level or remove a place, or flip the policy — a
 * workspace role does not grant it. That check runs inside `cti-identity-service`; nothing here
 * enforces it, so a caller must not decide from this module alone whom to show the mutating
 * controls to.
 */

export type SharePointPlaceLevel = 'site' | 'library' | 'folder' | 'file';
export type SharePointPlaceInteractiveAccess = 'read' | 'read_write';
export type SharePointPlaceAutomationAccess =
  'none' | SharePointPlaceInteractiveAccess;

/** One allowed place, as the SPA consumes it (camelCase). */
export interface SharePointPlace {
  id: string;
  connectionId: string;
  level: SharePointPlaceLevel;
  siteId: string;
  driveId: string | null;
  rootItemId: string | null;
  rootAncestorItemIds: string[];
  nameSnapshot: string;
  webUrlSnapshot: string | null;
  parentPathSnapshot: string | null;
  alias: string | null;
  interactiveAccess: SharePointPlaceInteractiveAccess;
  automationAccess: SharePointPlaceAutomationAccess;
  /** Whether the place could still be read off Graph the last time its snapshot was refreshed. */
  reachable: boolean;
}

/** A connection's whole policy: its places, whether it is enforced, and the ceiling on how many. */
export interface SharePointPlacePolicy {
  places: SharePointPlace[];
  enforced: boolean;
  placesLimit: number;
  /**
   * Whether the caller owns this connection — only an owner may add, rename, re-level, remove a
   * place, or flip the policy. Absent on an older server, which reads as `false`: fail-closed is the
   * only safe default here, since the opposite direction shows a mutating control to someone whose
   * click would 403.
   */
  isOwner: boolean;
}

/** The snake_case shape `PlacesController#serializePlace` returns. */
interface SharePointPlaceWire {
  id: string;
  connection_id: string;
  level: string;
  site_id: string;
  drive_id: string | null;
  root_item_id: string | null;
  root_ancestor_item_ids: string[];
  name_snapshot: string;
  web_url_snapshot: string | null;
  parent_path_snapshot: string | null;
  alias: string | null;
  interactive_access: string;
  automation_access: string;
  reachable: boolean;
}

function placeLevel(value: string): SharePointPlaceLevel {
  if (value === 'library' || value === 'folder' || value === 'file') {
    return value;
  }
  return 'site';
}

function placeInteractiveAccess(
  value: string,
): SharePointPlaceInteractiveAccess {
  return value === 'read_write' ? 'read_write' : 'read';
}

function placeAutomationAccess(value: string): SharePointPlaceAutomationAccess {
  if (value === 'read' || value === 'read_write') return value;
  return 'none';
}

function placeFromWire(wire: SharePointPlaceWire): SharePointPlace {
  return {
    id: wire.id,
    connectionId: wire.connection_id,
    level: placeLevel(wire.level),
    siteId: wire.site_id,
    driveId: wire.drive_id ?? null,
    rootItemId: wire.root_item_id ?? null,
    rootAncestorItemIds: wire.root_ancestor_item_ids ?? [],
    nameSnapshot: wire.name_snapshot,
    webUrlSnapshot: wire.web_url_snapshot ?? null,
    parentPathSnapshot: wire.parent_path_snapshot ?? null,
    alias: wire.alias,
    interactiveAccess: placeInteractiveAccess(wire.interactive_access),
    automationAccess: placeAutomationAccess(wire.automation_access),
    reachable: wire.reachable === true,
  };
}

async function throwSharePointPlaceError(response: Response): Promise<never> {
  throw new SharePointApiError(
    response.status,
    await errorMessage(response, 'SharePoint place request failed'),
  );
}

/** List a connection's allowed places, and whether the restriction is turned on at all. */
export async function listSharePointPlaces(
  workspaceId: string,
  connectionId: string,
): Promise<SharePointPlacePolicy> {
  const params = new URLSearchParams({
    workspace_id: workspaceId,
    connection_id: connectionId,
  });
  const response = await apiFetch(`${API_BASE}/places?${params.toString()}`);
  if (!response.ok) return throwSharePointPlaceError(response);
  const body = (await response.json()) as {
    places?: SharePointPlaceWire[];
    enforced?: boolean;
    places_limit?: number;
    is_owner?: boolean;
  };
  return {
    places: (body.places ?? []).map(placeFromWire),
    enforced: body.enforced === true,
    placesLimit: body.places_limit ?? 0,
    isOwner: body.is_owner === true,
  };
}

/** Rename a place or change either of its two access levels. Owner-only; identity enforces it. */
export async function updateSharePointPlace(
  placeId: string,
  patch: {
    workspaceId: string;
    alias?: string | null;
    interactiveAccess?: SharePointPlaceInteractiveAccess;
    automationAccess?: SharePointPlaceAutomationAccess;
  },
): Promise<SharePointPlace> {
  const response = await apiFetch(
    `${API_BASE}/places/${encodeURIComponent(placeId)}`,
    {
      method: 'PATCH',
      headers: new Headers({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({
        workspace_id: patch.workspaceId,
        ...(patch.alias !== undefined ? { alias: patch.alias } : {}),
        ...(patch.interactiveAccess !== undefined
          ? { interactive_access: patch.interactiveAccess }
          : {}),
        ...(patch.automationAccess !== undefined
          ? { automation_access: patch.automationAccess }
          : {}),
      }),
    },
  );
  if (!response.ok) return throwSharePointPlaceError(response);
  const body = (await response.json()) as { place: SharePointPlaceWire };
  return placeFromWire(body.place);
}

/** Remove a place from the allow-list. Owner-only; identity enforces it. */
export async function deleteSharePointPlace(
  placeId: string,
  workspaceId: string,
): Promise<void> {
  const params = new URLSearchParams({ workspace_id: workspaceId });
  const response = await apiFetch(
    `${API_BASE}/places/${encodeURIComponent(placeId)}?${params.toString()}`,
    { method: 'DELETE' },
  );
  if (!response.ok && response.status !== 204) {
    await throwSharePointPlaceError(response);
  }
}

/** Turn a connection's place restriction on or off. Owner-only; identity enforces it. */
export async function setSharePointPlacePolicy(
  workspaceId: string,
  connectionId: string,
  enforced: boolean,
): Promise<{ connectionId: string; enforced: boolean }> {
  const response = await apiFetch(`${API_BASE}/place-policy`, {
    method: 'PUT',
    headers: new Headers({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({
      workspace_id: workspaceId,
      connection_id: connectionId,
      enforced,
    }),
  });
  if (!response.ok) return throwSharePointPlaceError(response);
  const body = (await response.json()) as {
    connection_id: string;
    enforced: boolean;
  };
  return { connectionId: body.connection_id, enforced: body.enforced };
}

/** One knowledge row's address, for the server to walk up from. */
export interface SharePointCoverageItem {
  siteId: string;
  driveId: string;
  itemId: string;
}

/**
 * Whether each item sits inside one of the connection's allowed places, in the order given. The
 * server walks up from each item, which is what ids alone cannot do for a row nested under a folder
 * place. Only the rows the ids could not settle are ever sent.
 */
export async function checkSharePointPlaceCoverage(
  workspaceId: string,
  connectionId: string,
  items: readonly SharePointCoverageItem[],
): Promise<boolean[]> {
  const response = await apiFetch(`${API_BASE}/places/coverage`, {
    method: 'POST',
    headers: new Headers({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({
      workspace_id: workspaceId,
      connection_id: connectionId,
      items: items.map((item) => ({
        site_id: item.siteId,
        drive_id: item.driveId,
        item_id: item.itemId,
      })),
    }),
  });
  if (!response.ok) return throwSharePointPlaceError(response);
  const body = (await response.json()) as { inside?: unknown };
  if (!Array.isArray(body.inside) || body.inside.length !== items.length) {
    throw new Error('SharePoint place coverage answer was malformed');
  }
  return body.inside.map((inside) => inside === true);
}

/**
 * Query-key factory for one connection's allowed-places policy. Coverage sits under the policy key,
 * so anything that invalidates a connection's places re-asks its coverage too. A coverage key names
 * each item by site, drive and item id, because a Graph item id is only unique within its drive.
 */
export const sharePointPlaceKeys = {
  policy: (workspaceId: string, connectionId: string) =>
    ['sharepoint', 'places', workspaceId, connectionId] as const,
  coverage: (
    workspaceId: string,
    connectionId: string,
    items: readonly SharePointCoverageItem[],
  ) =>
    [
      ...sharePointPlaceKeys.policy(workspaceId, connectionId),
      'coverage',
      ...items.map((item) => [item.siteId, item.driveId, item.itemId]),
    ] as const,
};

/** TanStack query options for a connection's allowed-places policy. */
export function sharePointPlacesQueryOptions(
  workspaceId: string,
  connectionId: string,
) {
  return queryOptions({
    queryKey: sharePointPlaceKeys.policy(workspaceId, connectionId),
    queryFn: () => listSharePointPlaces(workspaceId, connectionId),
  });
}

/**
 * The add-place dialog's browsing: a short-lived capability that opens `cti-sync-service`'s browse
 * routes (sites, drives, items — the ones this client calls under it) for one connection, so the
 * person adding a place can see its whole reach even while the connection is already restricted to a
 * narrower set. The server also honours the capability on `search`, but nothing here calls it: a
 * file is added by browsing to it, and the picker has no search box.
 *
 * **The capability travels in this header, never in a query parameter.** A query parameter lands in
 * browser history, in `Referer` on every outbound link the picker renders, and in any access log
 * between here and the browser — none of which a log redaction can reach, because a redaction works
 * on headers. `onedrive.ts`'s `X-OneDrive-Setup-Token` is the exact same shape for the same reason;
 * match it, not the query string.
 */
export const SHAREPOINT_SETUP_TOKEN_HEADER = 'x-sharepoint-setup-token';

/** Headers carrying the setup capability, alongside whatever the caller already sends. */
function setupTokenHeaders(setupToken: string): Headers {
  return new Headers({ [SHAREPOINT_SETUP_TOKEN_HEADER]: setupToken });
}

/**
 * Mints a setup capability for one connection, scoped to the caller who asked for it — only the
 * connection's owner may (identity enforces this; nothing here checks it). It lives ten minutes and
 * opens browsing only: no file reading, no writing, no MCP tools.
 */
export async function createSharePointSetupSession(
  workspaceId: string,
  connectionId: string,
): Promise<{ token: string; expiresAt: string }> {
  const response = await apiFetch(`${API_BASE}/setup-sessions`, {
    method: 'POST',
    headers: new Headers({ 'Content-Type': 'application/json' }),
    body: JSON.stringify({
      workspace_id: workspaceId,
      connection_id: connectionId,
    }),
  });
  if (!response.ok) return throwSharePointPlaceError(response);
  const body = (await response.json()) as {
    token: string;
    expires_at: string;
  };
  return { token: body.token, expiresAt: body.expires_at };
}

/**
 * Lists sites under the setup capability. `connectionId` is required here, unlike the plain browse
 * call above — the capability is minted for one specific connection, and the service only serves the
 * unrestricted reach when the address it is asked about resolves to that same connection.
 */
export async function listSharePointSetupSites(
  workspaceId: string,
  connectionId: string,
  setupToken: string,
  search = '',
  options?: SharePointPageOptions,
): Promise<SharePointPage<SharePointSite>> {
  const params = browseParams(workspaceId, { ...options, connectionId });
  const trimmed = search.trim();
  if (trimmed) params.set('search', trimmed);
  const res = await apiFetch(`${API_BASE}/sites?${params.toString()}`, {
    headers: setupTokenHeaders(setupToken),
  });
  if (!res.ok)
    throw new SharePointApiError(
      res.status,
      await errorMessage(res, 'Failed to load SharePoint sites'),
    );
  const body = (await res.json()) as {
    sites?: SharePointSiteWire[];
    next_cursor?: string | null;
  };
  return {
    items: (body.sites ?? []).map((wire) => ({
      siteId: wire.site_id,
      name: wire.name,
      webUrl: wire.web_url,
    })),
    nextCursor: nextCursorOf(body),
  };
}

/** Lists a site's document libraries under the setup capability. See {@link listSharePointSetupSites}. */
export async function listSharePointSetupDrives(
  siteId: string,
  workspaceId: string,
  connectionId: string,
  setupToken: string,
  options?: SharePointPageOptions,
): Promise<SharePointPage<SharePointDrive>> {
  const params = browseParams(workspaceId, { ...options, connectionId });
  const res = await apiFetch(
    `${API_BASE}/sites/${encodeURIComponent(siteId)}/drives?${params.toString()}`,
    { headers: setupTokenHeaders(setupToken) },
  );
  if (!res.ok)
    throw new SharePointApiError(
      res.status,
      await errorMessage(res, 'Failed to load document libraries'),
    );
  const body = (await res.json()) as {
    drives?: SharePointDriveWire[];
    next_cursor?: string | null;
  };
  return {
    items: (body.drives ?? []).map((wire) => ({
      driveId: wire.drive_id,
      name: wire.name,
      webUrl: wire.web_url,
    })),
    nextCursor: nextCursorOf(body),
  };
}

/** Lists one level of a drive under the setup capability. See {@link listSharePointSetupSites}. */
export async function listSharePointSetupItems(
  driveId: string,
  parent: string,
  workspaceId: string,
  connectionId: string,
  setupToken: string,
  options?: SharePointPageOptions,
): Promise<SharePointPage<SharePointItem>> {
  const params = browseParams(workspaceId, { ...options, connectionId });
  params.set('parent', parent || 'root');
  const res = await apiFetch(
    `${API_BASE}/drives/${encodeURIComponent(driveId)}/items?${params.toString()}`,
    { headers: setupTokenHeaders(setupToken) },
  );
  if (!res.ok)
    throw new SharePointApiError(
      res.status,
      await errorMessage(res, 'Failed to load items'),
    );
  const body = (await res.json()) as {
    items?: SharePointItemWire[];
    next_cursor?: string | null;
  };
  return {
    items: (body.items ?? []).map((wire) => ({
      itemId: wire.item_id,
      name: wire.name,
      isFolder: wire.is_folder,
      webUrl: wire.web_url,
    })),
    nextCursor: nextCursorOf(body),
  };
}

/**
 * Searches files and folders by name and content under the setup capability — the same Microsoft
 * Search as {@link searchSharePointFiles}, but over the connection's whole reach, so the person
 * choosing places can find one that is not on the list yet.
 */
export async function searchSharePointSetupItems(
  query: string,
  workspaceId: string,
  connectionId: string,
  setupToken: string,
  options?: { limit?: number; offset?: number },
): Promise<SharePointSearchPage> {
  const params = new URLSearchParams({
    workspace_id: workspaceId,
    connection_id: connectionId,
    q: query.trim(),
    limit: String(options?.limit ?? SHAREPOINT_PAGE_SIZE),
    offset: String(options?.offset ?? 0),
  });
  const res = await apiFetch(`${API_BASE}/search?${params.toString()}`, {
    headers: setupTokenHeaders(setupToken),
  });
  if (!res.ok)
    throw new SharePointApiError(
      res.status,
      await errorMessage(res, 'Failed to search SharePoint'),
    );
  const body = (await res.json()) as {
    items?: SharePointSearchHitWire[];
    next_offset?: number | null;
  };
  return {
    items: (body.items ?? []).map((wire) => ({
      siteId: wire.site_id,
      driveId: wire.drive_id,
      parentId: wire.parent_id ?? '',
      itemId: wire.item_id,
      name: wire.name,
      isFolder: wire.is_folder,
      webUrl: wire.web_url,
    })),
    nextOffset: body.next_offset ?? null,
  };
}

/**
 * Where one site, library, folder or file lives, from its site down: what the place picker needs
 * to open the level that holds it. `ancestors` are the folders between the library root and the
 * target, outermost first.
 */
export interface SharePointLocated {
  site: SharePointSite;
  drive: SharePointDrive | null;
  ancestors: { itemId: string; name: string }[];
  /**
   * False when a folder above the target is closed to the connection's account — an item shared on
   * its own. `ancestors` then holds only the folders below that point, and there is no parent to open.
   */
  pathComplete: boolean;
  target: {
    level: SharePointPlaceLevel;
    id: string;
    name: string;
    webUrl: string;
  };
}

/**
 * Finds a place by a pasted link, or a folder or file by the ids a search hit carries, under the
 * setup capability. It reads Graph directly rather than through Microsoft Search, so it reaches a
 * site search leaves out. **400** means the text is not a SharePoint link, **404** that this
 * connection cannot open it; both carry the service's sentence.
 */
export async function locateSharePointPlace(
  address: { url: string } | { driveId: string; itemId: string },
  workspaceId: string,
  connectionId: string,
  setupToken: string,
): Promise<SharePointLocated> {
  const params = new URLSearchParams({
    workspace_id: workspaceId,
    connection_id: connectionId,
  });
  if ('url' in address) {
    params.set('url', address.url.trim());
  } else {
    params.set('drive_id', address.driveId);
    params.set('item_id', address.itemId);
  }
  const res = await apiFetch(`${API_BASE}/locate?${params.toString()}`, {
    headers: setupTokenHeaders(setupToken),
  });
  if (!res.ok)
    throw new SharePointApiError(
      res.status,
      await errorMessage(res, 'Could not open that link'),
    );
  const body = (await res.json()) as {
    site: SharePointSiteWire;
    drive: SharePointDriveWire | null;
    ancestors?: { item_id: string; name: string }[];
    path_complete?: boolean;
    target: {
      level: SharePointPlaceLevel;
      id: string;
      name: string;
      web_url: string;
    };
  };
  return {
    site: {
      siteId: body.site.site_id,
      name: body.site.name,
      webUrl: body.site.web_url,
    },
    drive: body.drive
      ? {
          driveId: body.drive.drive_id,
          name: body.drive.name,
          webUrl: body.drive.web_url,
        }
      : null,
    ancestors: (body.ancestors ?? []).map((folder) => ({
      itemId: folder.item_id,
      name: folder.name,
    })),
    pathComplete: body.path_complete !== false,
    target: {
      level: placeLevel(body.target.level),
      id: body.target.id,
      name: body.target.name,
      webUrl: body.target.web_url,
    },
  };
}

/** One place identity has not yet stored, as the SPA submits it. */
export interface SharePointPlaceCreateInput {
  workspaceId: string;
  connectionId: string;
  level: SharePointPlaceLevel;
  siteId: string;
  /** Required for every level but `site`. */
  driveId?: string;
  /** Required for `folder` and `file`: the item itself. */
  rootItemId?: string;
  alias?: string | null;
  interactiveAccess?: SharePointPlaceInteractiveAccess;
  automationAccess?: SharePointPlaceAutomationAccess;
}

/**
 * Adds one place to a connection's allow-list. Owner-only; identity enforces it — this call carries
 * the caller's platform JWT, never the setup capability, because creating the row is not part of what
 * the capability opens.
 */
export async function createSharePointPlace(
  input: SharePointPlaceCreateInput,
): Promise<SharePointPlace> {
  const body: Record<string, unknown> = {
    workspace_id: input.workspaceId,
    connection_id: input.connectionId,
    level: input.level,
    site_id: input.siteId,
  };
  if (input.driveId) body.drive_id = input.driveId;
  if (input.rootItemId) body.root_item_id = input.rootItemId;
  if (input.alias !== undefined) body.alias = input.alias;
  if (input.interactiveAccess !== undefined) {
    body.interactive_access = input.interactiveAccess;
  }
  if (input.automationAccess !== undefined) {
    body.automation_access = input.automationAccess;
  }
  const response = await apiFetch(`${API_BASE}/places`, {
    method: 'POST',
    headers: new Headers({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  });
  if (!response.ok) return throwSharePointPlaceError(response);
  const wire = (await response.json()) as { place: SharePointPlaceWire };
  return placeFromWire(wire.place);
}

/** The query every `/files` route takes: the workspace, and the connection when one is named. */
function fileQuery(workspaceId: string, connectionId: string | null): string {
  const params = new URLSearchParams({ workspace_id: workspaceId });
  if (connectionId) params.set('connection_id', connectionId);
  return params.toString();
}

/** The `/files` path of one SharePoint item. */
function filePath(driveId: string, itemId: string): string {
  return `${API_BASE}/files/drives/${encodeURIComponent(driveId)}/items/${encodeURIComponent(itemId)}`;
}

/**
 * One SharePoint file's metadata, read with the caller's own connection. Without a `connectionId` the
 * service reads it through any of the caller's connections that can. Its `etag` is what the viewer
 * polls to notice that the file changed.
 */
export async function getSharePointFileItem(
  workspaceId: string,
  connectionId: string | null,
  driveId: string,
  itemId: string,
): Promise<DriveFileItem> {
  const res = await apiFetch(
    `${filePath(driveId, itemId)}?${fileQuery(workspaceId, connectionId)}`,
  );
  if (!res.ok)
    throw new SharePointApiError(
      res.status,
      await errorMessage(res, 'Failed to read the file'),
    );
  const body = (await res.json()) as { item?: DriveFileItemWire };
  return driveFileItemFromWire(body.item ?? {});
}

/**
 * Upload a new file into a SharePoint folder (`parentItemId`, or `root` for the library's root). The
 * service refuses a name already taken in that folder rather than overwrite it.
 */
export async function createSharePointFile(input: {
  workspaceId: string;
  connectionId: string;
  driveId: string;
  parentItemId: string | null;
  name: string;
  content: Blob;
}): Promise<DriveFileItem> {
  const form = new FormData();
  form.set('workspace_id', input.workspaceId);
  form.set('connection_id', input.connectionId);
  form.set('name', input.name);
  form.set('file', input.content, input.name);
  const res = await apiFetch(
    `${filePath(input.driveId, input.parentItemId || 'root')}/children`,
    { method: 'POST', body: form },
  );
  if (!res.ok)
    throw new SharePointApiError(
      res.status,
      await errorMessage(res, 'Failed to upload the file'),
    );
  const body = (await res.json()) as { item?: DriveFileItemWire };
  return driveFileItemFromWire(body.item ?? {});
}

/** A SharePoint file's stored versions, newest first. */
export async function listSharePointFileVersions(
  workspaceId: string,
  connectionId: string | null,
  driveId: string,
  itemId: string,
): Promise<DriveFileVersionList> {
  const res = await apiFetch(
    `${filePath(driveId, itemId)}/versions?${fileQuery(workspaceId, connectionId)}`,
  );
  if (!res.ok)
    throw new SharePointApiError(
      res.status,
      await errorMessage(res, 'Failed to load the version history'),
    );
  return driveFileVersionsFromWire(await res.json());
}

/** The bytes of one stored version of a SharePoint file. */
export async function fetchSharePointFileVersionBlob(
  workspaceId: string,
  connectionId: string | null,
  driveId: string,
  itemId: string,
  versionId: string,
): Promise<Blob> {
  const res = await apiFetch(
    `${filePath(driveId, itemId)}/versions/${encodeURIComponent(versionId)}/content?${fileQuery(workspaceId, connectionId)}`,
  );
  if (!res.ok)
    throw new SharePointApiError(
      res.status,
      await errorMessage(res, 'Failed to load this version'),
    );
  return res.blob();
}

/**
 * Make an older version of a SharePoint file the current one. `ifMatch` is the eTag the caller last
 * read; a file changed since then is refused with 409 instead of overwritten.
 */
export async function restoreSharePointFileVersion(input: {
  workspaceId: string;
  connectionId: string | null;
  driveId: string;
  itemId: string;
  versionId: string;
  ifMatch: string;
}): Promise<DriveFileItem> {
  const body: Record<string, string> = {
    workspace_id: input.workspaceId,
    if_match: input.ifMatch,
  };
  if (input.connectionId) body.connection_id = input.connectionId;
  const res = await apiFetch(
    `${filePath(input.driveId, input.itemId)}/versions/${encodeURIComponent(input.versionId)}/restore`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );
  if (!res.ok)
    throw new DriveFileRestoreError(
      res.status,
      await driveErrorMessage(res, 'Failed to restore this version'),
    );
  const result = (await res.json()) as { item?: DriveFileItemWire };
  return driveFileItemFromWire(result.item ?? {});
}
